import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { AppLayout } from "@/layout/AppLayout";
import { AdminRoute } from "@/auth/AdminRoute";
import { VoiceLayout } from "@/pages/public/voice/VoiceLayout";
import {
  useUserFeatureFlags,
  useUserFeatureFlagsLoading,
} from "@/lib/feature-flags";

// Monitoring
const DashboardPage = lazy(
  () => import("@/pages/monitoring/dashboard/DashboardPage"),
);
const OneCallPage = lazy(
  () => import("@/pages/monitoring/one-call/OneCallPage"),
);
const OneToManyPage = lazy(
  () => import("@/pages/monitoring/one-to-many/OneToManyPage"),
);

// Config
const ConfigSetsPage = lazy(
  () => import("@/pages/config/config-sets/ConfigSetsPage"),
);
const FaqSetsPage = lazy(() =>
  import("@/pages/config/faq-sets/FaqSetsPage").then((m) => ({
    default: m.FaqSetsPage,
  })),
);
const TransfersPage = lazy(() =>
  import("@/pages/config/transfers/TransfersPage").then((m) => ({
    default: m.TransfersPage,
  })),
);
const CallGroupsPage = lazy(
  () => import("@/pages/config/call-groups/CallGroupsPage"),
);
const SIPNumbersPage = lazy(
  () => import("@/pages/config/sip-numbers/SIPNumbersPage"),
);
const Avatars3DPage = lazy(
  () => import("@/pages/config/avatars-3d/Avatars3DPage"),
);
const AvatarsLiveavatarPage = lazy(
  () => import("@/pages/config/avatars-liveavatar/AvatarsLiveavatarPage"),
);

// Data
const CustomersPage = lazy(
  () => import("@/pages/data/customers/CustomersPage"),
);
const CustomerDetailPage = lazy(
  () => import("@/pages/data/customers/CustomerDetailPage"),
);
const CallHistoryPage = lazy(
  () => import("@/pages/data/call-history/CallHistoryPage"),
);
const CallDetailPage = lazy(
  () => import("@/pages/data/call-history/CallDetailPage"),
);
const LearningsPage = lazy(
  () => import("@/pages/data/learnings/LearningsPage"),
);
const VoiceAuthAdminLayout = lazy(
  () => import("@/pages/data/voice-auth-admin/VoiceAuthAdminLayout"),
);
const VoiceAuthAccountsPage = lazy(
  () => import("@/pages/data/voice-auth-admin/AccountsPage"),
);
const VoiceAuthBootstrapPage = lazy(
  () => import("@/pages/data/voice-auth-admin/BootstrapPage"),
);
const StatisticsPage = lazy(
  () => import("@/pages/data/statistics/StatisticsPage"),
);
const A2aRequestsPage = lazy(
  () => import("@/pages/data/a2a-requests/A2aRequestsPage"),
);
// The admin's view of an A2A result IS the caller's page — see the route
// below, and the comment on the component itself.
const AgentResultPage = lazy(
  () => import("@/pages/public/agent-result/AgentResultPage"),
);

// Dev
const DebugPage = lazy(() => import("@/pages/dev/debug/DebugPage"));
const DatabasePage = lazy(() => import("@/pages/dev/database/DatabasePage"));

function ShellLayout() {
  return (
    <AppLayout>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </AppLayout>
  );
}

/**
 * Flat, single-shell router. The `AppLayout` wraps every authenticated
 * page; the `Outlet` swaps the body without remounting the chrome.
 * Admin-only routes are guarded inline via `<AdminRoute />`.
 */
export function AppRouter() {
  const flags = useUserFeatureFlags();
  const flagsLoading = useUserFeatureFlagsLoading();
  return (
    <Routes>
      {/* An A2A result, outside the app shell on purpose.
        An operator debugging a request needs to see what the caller saw, so
        this renders the caller's own page in the caller's own layout —
        sidebar, header and app chrome would make it a different artifact
        from the one being diagnosed. Admin-guarded all the same: it reads
        agent output for an arbitrary job id. */}
      <Route element={<AdminRoute />}>
        <Route
          path="admin/a2a-requests/:jobId/result"
          element={
            <VoiceLayout>
              <Suspense fallback={null}>
                <AgentResultPage />
              </Suspense>
            </VoiceLayout>
          }
        />
      </Route>

      <Route element={<ShellLayout />}>
        <Route
          index
          element={<Navigate to="/monitoring/dashboard" replace />}
        />

        {/* Monitoring (visible to all authenticated users) */}
        <Route path="monitoring/dashboard" element={<DashboardPage />} />
        <Route path="monitoring/one-call" element={<OneCallPage />} />
        <Route path="monitoring/one-to-many" element={<OneToManyPage />} />

        {/* Admin */}
        <Route element={<AdminRoute />}>
          <Route path="admin/config-sets" element={<ConfigSetsPage />} />
          <Route path="admin/faq-sets" element={<FaqSetsPage />} />
          <Route path="admin/transfers" element={<TransfersPage />} />
          <Route path="admin/call-groups" element={<CallGroupsPage />} />
          <Route path="admin/sip-numbers" element={<SIPNumbersPage />} />
          {flags.avatars3d && (
            <Route path="admin/avatars-3d" element={<Avatars3DPage />} />
          )}
          {flags.avatarsLiveavatar && (
            <Route
              path="admin/avatars-liveavatar"
              element={<AvatarsLiveavatarPage />}
            />
          )}
          <Route path="admin/customers" element={<CustomersPage />} />
          <Route path="admin/customers/:id" element={<CustomerDetailPage />} />
          <Route path="admin/call-history" element={<CallHistoryPage />} />
          <Route path="admin/call-history/:id" element={<CallDetailPage />} />
          <Route path="admin/learnings" element={<LearningsPage />} />
          {flags.voiceAuth && (
            <Route path="admin/voice-auth" element={<VoiceAuthAdminLayout />}>
              <Route index element={<VoiceAuthAccountsPage />} />
              <Route path="bootstrap" element={<VoiceAuthBootstrapPage />} />
            </Route>
          )}
          <Route path="admin/a2a-requests" element={<A2aRequestsPage />} />
          <Route path="admin/statistics" element={<StatisticsPage />} />
          <Route path="admin/debug" element={<DebugPage />} />
          <Route path="admin/database" element={<DatabasePage />} />
        </Route>

        {/* Catch-all: send unknown paths back to the dashboard. Held back
          while the flags load — until then the gated routes above don't exist,
          so a deep link to one would be redirected away before it could
          match. */}
        <Route
          path="*"
          element={
            flagsLoading ? (
              <></>
            ) : (
              <Navigate to="/monitoring/dashboard" replace />
            )
          }
        />
      </Route>
    </Routes>
  );
}
